export type SubmissionChannel =
  | "MEF"
  | "SUBMIT_FORMS_ONLINE"
  | "DOC_UPLOAD_TOOL"
  | "TAX_PRO_ACCOUNT"
  | "MAIL";

export type SubmissionStatus =
  | "DRAFT"
  | "QUEUED"
  | "TRANSMITTED"
  | "ACCEPTED"
  | "REJECTED"
  | "ERROR"
  | "CANCELLED";

export type SubmissionItemKind =
  | "RETURN"
  | "POA"
  | "NOTICE_RESPONSE"
  | "ATTACHMENT";

export interface SubmissionItem {
  id: string;
  kind: SubmissionItemKind;
  position: number;
  document: string | null;
  formSubmission: string | null;
  label: string;
  formNumber: string;
}

export interface SubmissionEvent {
  id: string;
  eventType: string;
  eventTypeDisplay: string;
  isSystem: boolean;
  detail: string;
  createdAt: string;
}

export interface CredentialsReady {
  efin: boolean;
  caf: boolean;
}

export interface IRSSubmission {
  id: string;
  client: string;
  case: string | null;
  channel: SubmissionChannel;
  channelDisplay: string;
  status: SubmissionStatus;
  statusDisplay: string;
  practitioner: string;
  practitionerName: string;
  practitionerCredentialsReady: CredentialsReady;
  irsConfirmationNumber: string;
  irsAckStatus: string;
  irsAckCode: string;
  errorMessage: string;
  receiptDocument: string | null;
  submittedAt: string | null;
  transmittedAt: string | null;
  ackedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: SubmissionItem[];
}

export interface IRSSubmissionDetail extends IRSSubmission {
  events: SubmissionEvent[];
}

export interface NewSubmissionItem {
  document?: string;
  form_submission?: string;
  kind: SubmissionItemKind;
}

export interface NewSubmissionPayload {
  channel: SubmissionChannel;
  case?: string | null;
  items: NewSubmissionItem[];
}

export interface EFilingStatus {
  efilingEnabled: boolean;
  efilingDown: boolean;
  efilingMessage: string;
  connectorConfigured: boolean;
  queuedCount: number;
  transmittedCount: number;
  pendingAckCount: number;
}

/** User-facing channel options for the New Submission picker. */
export const CHANNELS: { value: SubmissionChannel; label: string; help: string }[] = [
  {
    value: "MEF",
    label: "Modernized e-File (returns)",
    help: "Electronically file a tax return. Requires the assigned practitioner to have an EFIN and CAF number on file.",
  },
  {
    value: "SUBMIT_FORMS_ONLINE",
    label: "Submit Forms 2848/8821 Online",
    help: "File a power of attorney or authorization. Requires a CAF number.",
  },
  {
    value: "DOC_UPLOAD_TOOL",
    label: "Document Upload Tool",
    help: "Respond to an IRS notice. Requires the access code printed on the notice.",
  },
  {
    value: "TAX_PRO_ACCOUNT",
    label: "Tax Pro Account (digital POA/TIA)",
    help: "Submit a digital authorization request. Requires a CAF number.",
  },
  {
    value: "MAIL",
    label: "Mail / other (guided manual)",
    help: "Track a filing you mail or submit yourself; record the confirmation here.",
  },
];

export const ITEM_KINDS: { value: SubmissionItemKind; label: string }[] = [
  { value: "RETURN", label: "Tax return" },
  { value: "POA", label: "Power of attorney" },
  { value: "NOTICE_RESPONSE", label: "Notice response" },
  { value: "ATTACHMENT", label: "Attachment" },
];

export const STATUS_TONE: Record<SubmissionStatus, string> = {
  DRAFT: "bg-muted text-muted-foreground",
  QUEUED: "bg-primary/10 text-primary",
  TRANSMITTED: "bg-primary/15 text-primary",
  ACCEPTED: "bg-success/15 text-success",
  REJECTED: "bg-destructive/15 text-destructive",
  ERROR: "bg-destructive/15 text-destructive",
  CANCELLED: "bg-muted text-muted-foreground",
};
